import { useState } from 'react';
import { DollarSign, Calendar, Building2, ArrowRight, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { upworkProjects } from '@/data/portfolio';
import { Link } from 'react-router-dom';
import { TiltCard } from '@/components/ui/TiltCard';

const projectTypes = ['All', 'Web App', 'Mobile App', 'AI/ML', 'Full Stack'];

export function ProjectsShowcase() {
  const [selectedType, setSelectedType] = useState('All');

  const filteredProjects = selectedType === 'All'
    ? upworkProjects
    : upworkProjects.filter(p => p.type === selectedType);

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-primary" />
            <span className="text-primary font-semibold tracking-wider uppercase text-sm">
              Featured Work
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Enterprise Projects
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Explore our high-value enterprise projects delivered through Upwork and direct partnerships
          </p>
        </div>

        {/* Type Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {projectTypes.map((type) => (
            <Button
              key={type}
              variant={selectedType === type ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedType(type)}
              className="rounded-full"
            >
              {type}
            </Button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filteredProjects.map((project, index) => (
            <TiltCard
              key={project.title}
              className="glass-card-hover rounded-2xl p-8 transition-all duration-300 h-full"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full mb-3">
                    {project.type}
                  </span>
                  <h3 className="text-2xl font-bold text-foreground">{project.title}</h3>
                </div>
              </div>

              {/* Meta Info */}
              <div className="flex flex-wrap gap-4 mb-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  <span>{project.budget}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span>{project.duration}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="w-4 h-4 text-purple-500" />
                  <span>{project.industry}</span>
                </div>
              </div>

              {/* Description */}
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {project.description}
              </p>

              {/* Who It Serves */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-foreground mb-2">Who It Serves</h4>
                <p className="text-sm text-muted-foreground">{project.whoItServes}</p>
              </div>

              {/* What It Delivers */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-2">What It Delivers</h4>
                <p className="text-sm text-muted-foreground">{project.whatItDelivers}</p>
              </div>

              {/* Technologies */}
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((tech) => (
                  <span
                    key={tech}
                    className="px-3 py-1 bg-primary/5 text-primary/80 text-xs font-medium rounded-full border border-primary/10"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </TiltCard>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link to="/case-studies/">
            <Button variant="default" size="lg" className="group">
              View All Case Studies
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
